"use client";

import {
  Itineraries,
  LoyaltyAccount,
  LoyaltyAccountsResponse,
  LoyaltyProgram,
  UserStatus,
  UsersUserIdWelcomeActionsGetRequest,
  WelcomeActions,
  User,
  CurrencyAmount,
  UserCreditsSlim,
} from "@/generated/email_parser.openapi";
import { formatCurrency } from "@/lib/utils";
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { filterLoyaltyAccounts } from "../loyalty-dashboard/helpers";
import { GondolaLoading } from "@/components/ui/gondolaLoading";
import {
  GmailReSyncModal,
  RatePastBookingModal,
} from "@/components/modal/welcomeActionsModal";
import {
  SearchSessionCollection,
  SearchSessionV2,
} from "@/generated/search.openapi";
import { Session } from "next-auth";
import { signOut } from "next-auth/react";
import { usePostHog } from "posthog-js/react";

export type EmailData = {
  email: string;
  cashValue: number;
};

type GlobalStoreContextType = {
  user?: {
    data: User;
    status: UserStatus;
    fetchUser?: () => Promise<User | undefined>;
    isTemporaryFullAccess?: boolean;
    setIsTemporaryFullAccess: Dispatch<SetStateAction<boolean>>;
  };
  loyaltyData: {
    totalLoyaltyAccounts: number;
    loyaltyAccounts: LoyaltyAccount[];
    allLoyaltyAccountsIncludingDeleted: LoyaltyAccount[];
    activeLoyaltyAccounts: LoyaltyAccount[];
    loyaltyPrograms: LoyaltyProgram[];
    refetchLoyaltyAccounts: (
      includeDeleted?: boolean
    ) => Promise<LoyaltyAccountsResponse | undefined>;
    refetchAllLoyaltyAccountsIncludingDeleted: () => Promise<
      LoyaltyAccountsResponse | undefined
    >;
    refetchAllLoyaltyData: () => Promise<void>;
    totalCash?: string;
    totalPointsUpdate?: {
      string: string;
      value: number;
    };
    allEmailData: EmailData[];
    hasMultipleEmails: boolean;
    loyaltyAccountsLoading: boolean;
  };
  userCreditsData: {
    totalCashValue: number;
    totalCashValueString: string;
    userCredits?: UserCreditsSlim;
  };
  tripsData: {
    fetchItineraries: () => Promise<Itineraries | undefined>;
    itineraries: Itineraries | undefined;
  };

  refetchDataPostBooking: () => void;
  welcomeActions: {
    data?: WelcomeActions;
    refetchWelcomeActions: () => Promise<WelcomeActions | undefined>;
    checkAndOpenGmailReSyncModal: () => void;
    checkAndOpenRatingsModal: () => void;
  };
  searchData: {
    recentSearchSessions: SearchSessionV2[];
    hasAlreadyFetched: boolean;
    fetchRecentSearchSession: () => Promise<SearchSessionV2[] | undefined>;
  };
  isEventPage: boolean;
};

const defaultState: GlobalStoreContextType = {
  user: undefined,
  loyaltyData: {
    totalLoyaltyAccounts: 0,
    loyaltyAccounts: [],
    allLoyaltyAccountsIncludingDeleted: [],
    activeLoyaltyAccounts: [],
    loyaltyPrograms: [],
    refetchLoyaltyAccounts: async () => undefined,
    refetchAllLoyaltyAccountsIncludingDeleted: async () => undefined,
    refetchAllLoyaltyData: async () => {},
    totalCash: "",
    totalPointsUpdate: {
      string: "",
      value: 0,
    },
    allEmailData: [],
    hasMultipleEmails: false,
    loyaltyAccountsLoading: true,
  },
  userCreditsData: {
    totalCashValue: 0,
    totalCashValueString: "",
    userCredits: undefined,
  },

  tripsData: {
    fetchItineraries: async () => undefined,
    itineraries: undefined,
  },
  refetchDataPostBooking: () => {},
  welcomeActions: {
    data: undefined,
    refetchWelcomeActions: async () => undefined,
    checkAndOpenGmailReSyncModal: () => {},
    checkAndOpenRatingsModal: () => {},
  },
  searchData: {
    recentSearchSessions: [],
    hasAlreadyFetched: false,
    fetchRecentSearchSession: async () => undefined,
  },
  isEventPage: false,
};

const GlobalStoreContext = createContext<GlobalStoreContextType>(defaultState);

export default function GlobalStoreProvider({
  children,
  session,
}: PropsWithChildren<{
  session: Session | null;
}>) {
  const [loyaltyAccounts, setLoyaltyAccounts] = useState<LoyaltyAccount[]>([]);
  const [
    allLoyaltyAccountsIncludingDeleted,
    setAllLoyaltyAccountsIncludingDeleted,
  ] = useState<LoyaltyAccount[]>([]);
  const [loyaltyPrograms, setLoyaltyPrograms] = useState<LoyaltyProgram[]>([]);
  const [totalLoyaltyAccounts, setTotalLoyaltyAccounts] = useState(0);
  const [activeLoyaltyAccounts, setActiveLoyaltyAccounts] = useState<
    LoyaltyAccount[]
  >([]);
  const [loyaltyAccountsLoading, setLoyaltyAccountsLoading] = useState(
    defaultState.loyaltyData.loyaltyAccountsLoading
  );
  const [userCredits, setUserCredits] = useState<UserCreditsSlim>();
  const [initialLoading, setInitialLoading] = useState(true);

  const [itineraries, setItineraries] = useState<Itineraries | undefined>();
  const [welcomeActions, setWelcomeActions] = useState<WelcomeActions>();
  const [gmailReSyncModalOpen, setGmailReSyncModalOpen] = useState(false);
  const [ratePastStayActions, setRatePastStayActions] = useState(false);
  const [recentSearchSessions, setRecentSearchSessions] = useState<
    SearchSessionV2[]
  >([]);
  const [userLoading, setUserLoading] = useState(true);
  const [initUserCall, setInitUserCall] = useState(false);
  const [userData, setUserData] = useState<User>();
  const [hasAlreadyFetched, setHasAlreadyFetched] = useState(false);
  const [totalCashValue, setTotalCashValue] = useState<CurrencyAmount>();
  const [pointsUpdateTotal, setPointsUpdateTotal] = useState<CurrencyAmount>();
  const [isTemporaryFullAccess, setIsTemporaryFullAccess] = useState(false);
  const isEventPage = Boolean(
    typeof window !== "undefined" && window.location.pathname.includes("/event")
  );
  const posthog = usePostHog();

  const fetchUser = useCallback(async () => {
    try {
      const userData = (await fetch("/api/user/self").then((res) =>
        res.json()
      )) as User;
      setUserData(userData);
      return userData;
    } catch (error) {
      posthog?.reset();
      signOut({ callbackUrl: "/" });
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserCredits = useCallback(async () => {
    try {
      const userCredits = (await fetch("/api/user/credits").then((res) =>
        res.json()
      )) as UserCreditsSlim;
      setUserCredits(userCredits);
      return userCredits;
    } catch (error) {
      return;
    }
  }, []);

  const refetchLoyaltyAccounts = useCallback(
    async (includeDeleted: boolean = false) => {
      try {
        const url = includeDeleted
          ? `/api/user/loyalty_accounts/v2/get?extended=false`
          : `/api/user/loyalty_accounts/v2/get`;

        const result = (await fetch(url).then((data) => {
          return data.json();
        })) as LoyaltyAccountsResponse;

        if (includeDeleted) {
          // When fetching all accounts including deleted, only update the deleted accounts list
          setAllLoyaltyAccountsIncludingDeleted(result.items || []);
        } else {
          // When fetching regular accounts, update all the main state
          setPointsUpdateTotal(result.pointsUpdateTotal);
          setTotalCashValue(result.totalCashValue);
          const activeAccounts = result.items?.filter(filterLoyaltyAccounts);
          setActiveLoyaltyAccounts(activeAccounts || []);
          // Use Math.max to handle edge cases where accountTotal might be 0 but there are items
          setTotalLoyaltyAccounts(
            Math.max(result.accountTotal ?? 0, result.items?.length ?? 0)
          );
          setLoyaltyAccounts(result.items || []);
        }

        return result;
      } catch (error) {
        return;
      }
    },
    []
  );

  const refetchAllLoyaltyAccountsIncludingDeleted = useCallback(async () => {
    return refetchLoyaltyAccounts(true);
  }, [refetchLoyaltyAccounts]);

  const refetchAllLoyaltyData = useCallback(async () => {
    // Refresh both regular and deleted accounts
    await Promise.all([
      refetchLoyaltyAccounts(false), // Regular accounts
      refetchLoyaltyAccounts(true), // Including deleted
    ]);
  }, [refetchLoyaltyAccounts]);

  const fetchLoyaltyPrograms = useCallback(async () => {
    try {
      // Use public endpoint for non-authenticated users
      const endpoint = session?.user
        ? `/api/loyalty_programs`
        : `/api/public/loyalty_programs`;
      const result = (await fetch(endpoint).then((data) => {
        return data.json();
      })) as LoyaltyProgram[];
      setLoyaltyPrograms(result || []);
      return result;
    } catch (error) {
      return;
    }
  }, [session?.user]);

  const fetchItineraries = useCallback(async () => {
    try {
      const data = (await fetch(`/api/user/trips/v2`).then((data) =>
        data.json()
      )) as Itineraries;
      setItineraries(data);
      return data;
    } catch (error) {
      return;
    }
  }, []);

  const fetchRecentSearchSession = useCallback(async () => {
    try {
      const response = (await fetch(`/api/search/hotel/recent`).then((data) =>
        data.json()
      )) as SearchSessionCollection;
      const data = response.items || [];
      setRecentSearchSessions(data);
      return data;
    } catch (error) {
      return;
    } finally {
      setHasAlreadyFetched(true);
    }
  }, []);

  const refetchDataPostBooking = () => {
    fetchUserCredits();
    fetchItineraries();
  };

  const fetchWelcomeActions = useCallback(async () => {
    try {
      if (!session?.user.id) return;
      const request: UsersUserIdWelcomeActionsGetRequest = {
        userId: session?.user.id,
      };
      const response = (await fetch("/api/user/welcome_actions", {
        method: "POST",
        body: JSON.stringify(request),
      }).then((res) => res.json())) as WelcomeActions;
      setWelcomeActions(response);
      return response;
    } catch (error) {
      return;
    }
  }, [session?.user.id]);

  const checkAndOpenGmailReSyncModal = useCallback(() => {
    const hasGmailReSyncAction = Boolean(
      welcomeActions?.emailSyncTokenExpiredWelcomeAction?.id
    );
    if (hasGmailReSyncAction) {
      setGmailReSyncModalOpen(true);
    }
  }, [welcomeActions]);

  const checkAndOpenRatingsModal = useCallback(() => {
    const hasPastRatingAction = Boolean(
      (welcomeActions?.ratePastStayWelcomeActions.items.length ?? 0) > 0
    );
    setRatePastStayActions(hasPastRatingAction);
  }, [welcomeActions]);

  const canFetchData = useMemo(() => {
    if (userLoading && Boolean(session?.user)) return "loading";
    return (
      Boolean(session?.user) &&
      (userData?.status === "EMAIL_SYNCED" ||
        userData?.status === "SITE_ACCESS_FULL" ||
        (userData?.status === "SITE_ACCESS" && isEventPage))
    );
  }, [session?.user, userData?.status, userLoading, isEventPage]);

  useEffect(() => {
    if (canFetchData === "loading") return;
    if (canFetchData) {
      fetchWelcomeActions();
    }
  }, [fetchWelcomeActions, canFetchData]);

  useEffect(() => {
    if (Boolean(session?.user) && !initUserCall) {
      setInitUserCall(true);
      (async () => {
        setUserLoading(true);
        await fetchUser();
        setUserLoading(false);
      })();
    }
  }, [fetchUser, initUserCall, session]);

  useEffect(() => {
    if (canFetchData === "loading") return;
    setLoyaltyAccountsLoading(true);

    (async () => {
      try {
        if (canFetchData) {
          await refetchLoyaltyAccounts();
        }
        await fetchLoyaltyPrograms();
        setLoyaltyAccountsLoading(false);
        if (canFetchData) {
          fetchUserCredits();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error fetching loyalty data:", error);
      } finally {
        setInitialLoading(false);
      }
    })();
  }, [
    refetchLoyaltyAccounts,
    fetchUserCredits,
    fetchLoyaltyPrograms,
    canFetchData,
    session?.user,
    userLoading,
  ]);

  useEffect(() => {
    if (!userLoading) {
      setInitialLoading(false);
    }
  }, [userLoading]);

  const totalCashValWithCurrency = useMemo(
    () =>
      formatCurrency(
        totalCashValue?.value ?? 0,
        totalCashValue?.currency ?? "USD",
        0
      ),
    [totalCashValue]
  );

  const allEmailData: EmailData[] = useMemo(() => {
    if (!loyaltyAccounts || loyaltyAccounts.length === 0) {
      if (userData?.status === UserStatus.SiteAccessFull) {
        return [{ email: userData?.email as string, cashValue: 0 }];
      }
      return [];
    }
    const emailArr = loyaltyAccounts
      ?.map((account) => account.account?.email ?? userData?.email)
      .filter((value, index, self) => self.indexOf(value) === index)
      .filter(Boolean) as string[];

    return emailArr.map((email) => {
      return {
        email,
        cashValue: loyaltyAccounts.reduce((acc, curr) => {
          if (curr.account?.email === email) {
            return acc + (curr.cashValue || 0);
          }
          return acc;
        }, 0),
      };
    });
  }, [loyaltyAccounts, userData]);

  const loyaltyData = useMemo(
    () => ({
      loyaltyAccounts,
      allLoyaltyAccountsIncludingDeleted,
      totalLoyaltyAccounts,
      activeLoyaltyAccounts,
      loyaltyPrograms,
      refetchLoyaltyAccounts,
      refetchAllLoyaltyAccountsIncludingDeleted,
      refetchAllLoyaltyData,
      totalCash: totalCashValWithCurrency,
      totalPointsUpdate: pointsUpdateTotal
        ? {
            string: formatCurrency(
              pointsUpdateTotal.value,
              pointsUpdateTotal.currency,
              0
            ),
            value: pointsUpdateTotal.value,
          }
        : undefined,
      allEmailData,
      hasMultipleEmails: allEmailData.length > 1,
      loyaltyAccountsLoading,
    }),
    [
      loyaltyAccounts,
      allLoyaltyAccountsIncludingDeleted,
      totalLoyaltyAccounts,
      activeLoyaltyAccounts,
      loyaltyPrograms,
      refetchLoyaltyAccounts,
      refetchAllLoyaltyAccountsIncludingDeleted,
      refetchAllLoyaltyData,
      totalCashValWithCurrency,
      pointsUpdateTotal,
      allEmailData,
      loyaltyAccountsLoading,
    ]
  );

  return (
    <GlobalStoreContext.Provider
      value={{
        user: userData
          ? {
              data: userData,
              status: userData.status as UserStatus,
              fetchUser,
              isTemporaryFullAccess,
              setIsTemporaryFullAccess,
            }
          : undefined,
        loyaltyData: loyaltyData,
        userCreditsData: {
          totalCashValue: totalCashValue?.value ?? 0,
          totalCashValueString: totalCashValWithCurrency,
          userCredits,
        },

        tripsData: {
          fetchItineraries,
          itineraries,
        },
        refetchDataPostBooking,
        welcomeActions: {
          data: welcomeActions,
          refetchWelcomeActions: fetchWelcomeActions,
          checkAndOpenGmailReSyncModal,
          checkAndOpenRatingsModal,
        },
        searchData: {
          recentSearchSessions,
          hasAlreadyFetched,
          fetchRecentSearchSession,
        },
        isEventPage,
      }}
    >
      {initialLoading ? (
        <div className="h-screen w-screen place-content-center bg-background">
          <GondolaLoading />
        </div>
      ) : (
        <>
          <GmailReSyncModal
            open={gmailReSyncModalOpen}
            onOpenChange={setGmailReSyncModalOpen}
          />
          {!gmailReSyncModalOpen && (
            <RatePastBookingModal
              open={ratePastStayActions}
              onOpenChange={setRatePastStayActions}
            />
          )}
          {children}
        </>
      )}
    </GlobalStoreContext.Provider>
  );
}

export const useGlobalStore = () => {
  const {
    user,
    loyaltyData,
    userCreditsData,
    tripsData,
    refetchDataPostBooking,
    welcomeActions,
    searchData,
    isEventPage,
  } = useContext(GlobalStoreContext);

  return {
    user,
    loyaltyData,
    userCreditsData,
    tripsData,
    refetchDataPostBooking,
    welcomeActions,
    searchData,
    isEventPage,
  };
};
